// The uploaded document (docs/10 §3/§5) and the one function that turns a
// queued record into bytes.
//
// ── THE SHAPE IS THE SERVER'S ──────────────────────────────────────────────
// `backend/internal/diag` decodes with `DisallowUnknownFields` and validates
// every payload against a per-event spec: an extra key anywhere costs the WHOLE
// report, not the key. So this file mirrors `diag.Report` exactly —
// `{reportId, app, net, events[{seq, at, level, event, payload}]}` — and the
// tests below pin that correspondence.
//
// `frontend/protocol` does not yet carry this schema (commit 0520f0d added
// `DiagIntake`, i.e. where a report goes, but nothing describing what is sent),
// so it lives here in ONE place until it can be promoted. The job report asks
// for that promotion, because two hand-maintained copies of a wire shape is
// exactly the drift the protocol package exists to prevent.
// ───────────────────────────────────────────────────────────────────────────
//
// Everything on the document is a constant, a number, an enum member, or a
// value that went through `redact.ts`. `renderUpload` is a PURE function of the
// stored record — no clock, no randomness — which is what lets "preview"
// promise byte-identity with the upload rather than merely resemblance (§4: an
// anonymisation promise the user cannot inspect is not a promise).

import {
  coarseMinute,
  coarseSecond,
  containsResidue,
  pick,
  safeCarrier,
  safeCommit,
  safeCountry,
  safeVersion,
} from './redact.js';
import { NETWORK_KINDS, levelOf, type DiagEvent, type NetworkKind } from './events.js';
import type { LogLine } from '../logRing.js';

/** Why this report exists. Kept on the RECORD, not the wire — the server has
 *  no field for it (see the cross-boundary asks in the job report). */
export const REPORT_REASONS = ['auto', 'severe', 'manual'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/** How a delivery attempt travelled (docs/10 §5, the ladder). */
export const DELIVERY_VIA = ['ws', 'rest', 'intake', 'user'] as const;
export type DeliveryVia = (typeof DELIVERY_VIA)[number];

/** How it went. Codes and kinds; never a server sentence. */
export const DELIVERY_OUTCOMES = [
  'ok',
  'timeout',
  'rejected',
  'frozen',
  'status',
  'unavailable',
  'other',
] as const;
export type DeliveryOutcome = (typeof DELIVERY_OUTCOMES)[number];

export interface DeliveryAttempt {
  /** Unix seconds, bucketed to the minute. */
  at: number;
  via: DeliveryVia;
  outcome: DeliveryOutcome;
}

/**
 * Coarse device context. Every field is OS-owned and supplied by the host
 * (docs/10 §1); the brain never infers country from a connection, because that
 * measures the VPN exit node and mislabels exactly the population worth
 * understanding (§3).
 */
export interface DeviceContext {
  platform?: string | undefined;
  osMajor?: number | string | undefined;
  appVersion?: string | undefined;
  /** ISO-3166 alpha-2, read from telephony on-device — never from location. */
  country?: string | undefined;
  /** MCC+MNC. Unavailable on iOS since CTCarrier was deprecated — an honest gap. */
  carrier?: string | undefined;
  networkKind?: NetworkKind | string | undefined;
  /** So blocking statistics can EXCLUDE tunnelled sessions, which describe
   *  someone else's network entirely (§3). Not a judgement about VPN use. */
  vpnActive?: boolean | undefined;
}

export interface BuildContext {
  version?: string | undefined;
  commit?: string | undefined;
  /** `jsc | webview | web` — the brain's own binding, and the most reliable
   *  statement of platform available without asking the host anything. */
  entry?: string | undefined;
}

/** One event, with the device-clock time it happened at. */
export interface ReportEvent {
  /** Device-clock ms. Coarsened to UNIX SECONDS on the way into the document. */
  at: number;
  payload: DiagEvent;
}

/** `diag.Event` (backend/internal/diag/diag.go), field for field. */
export interface WireEvent {
  seq: number;
  /** UNIX SECONDS. A report never carries a millisecond (§3). */
  at: number;
  level: string;
  event: string;
  payload: Record<string, unknown>;
}

/** `diag.Report`, field for field. Nothing else may appear on the wire. */
export interface ReportBody {
  /** Fresh 128-bit random per upload, lowercase hex, and nothing else — two
   *  reports from one device cannot be linked by the server (§3). */
  reportId: string;
  app: {
    version: string;
    brainCommit: string;
    platform: string;
    osMajor: number;
  };
  net: {
    country: string;
    carrier: string;
    kind: NetworkKind;
    vpnActive: boolean;
  };
  events: WireEvent[];
}

/** `diag.platforms`. `other` is deliberately absent server-side: these are the
 *  three brains, and an unknown platform is a bug, not a category. */
const PLATFORMS = ['android', 'ios', 'web'] as const;
export type Platform = (typeof PLATFORMS)[number];

/** Which brain binding runs on which platform. */
const PLATFORM_OF_ENTRY: Record<string, Platform> = {
  jsc: 'ios',
  webview: 'android',
  web: 'web',
};

/** `commitRe` has no empty case, so an unstamped dev build needs a value that
 *  is obviously not a commit rather than one that looks like a real build. */
const UNKNOWN_COMMIT = '0000000';
/** `versionRe` likewise. */
const UNKNOWN_VERSION = 'unknown';

function osMajorOf(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.min(Math.floor(value), 999);
  }
  if (typeof value === 'string') {
    const match = /^(\d{1,3})/.exec(value.trim());
    if (match) return Number(match[1]);
  }
  return 0;
}

function platformOf(device: DeviceContext, build: BuildContext): Platform {
  if (typeof device.platform === 'string') {
    const named = PLATFORMS.find((p) => p === device.platform);
    if (named) return named;
  }
  // The brain's own binding is a better answer than a host's typo.
  return (build.entry !== undefined ? PLATFORM_OF_ENTRY[build.entry] : undefined) ?? 'web';
}

export interface BuildReportInput {
  reportId: string;
  reason: ReportReason;
  device: DeviceContext;
  build: BuildContext;
  events: readonly ReportEvent[];
}

/** Assemble the document. Total: a hostile `device` block cannot widen it. */
export function buildReport(input: BuildReportInput): ReportBody {
  return {
    reportId: input.reportId,
    app: {
      version: safeVersion(input.device.appVersion) ?? safeVersion(input.build.version) ?? UNKNOWN_VERSION,
      brainCommit: safeCommit(input.build.commit) ?? UNKNOWN_COMMIT,
      platform: platformOf(input.device, input.build),
      osMajor: osMajorOf(input.device.osMajor),
    },
    net: {
      // The server reads "" as unknown; `null` would be a second spelling of
      // the same thing and it validates only one.
      country: safeCountry(input.device.country) ?? '',
      carrier: safeCarrier(input.device.carrier) ?? '',
      kind: pick(input.device.networkKind, NETWORK_KINDS, 'unknown'),
      vpnActive: input.device.vpnActive === true,
    },
    events: input.events.map((e, seq) => toWireEvent(e, seq)),
  };
}

/** One structured event as the server's `{seq, at, level, event, payload}`. */
function toWireEvent(e: ReportEvent, seq: number): WireEvent {
  const { event, ...payload } = e.payload;
  return {
    seq,
    at: coarseSecond(e.at),
    level: levelOf(e.payload),
    event,
    payload: payload as Record<string, unknown>,
  };
}

/** A queued record: the document plus its own delivery history (docs/10 §5). */
export interface QueuedReport {
  id: string;
  body: ReportBody;
  /** Why it was cut. Device-local: the intake has no field for it. */
  reason: ReportReason;
  /** Unix seconds (minute bucket) the report was queued. */
  queuedAt: number;
  attempts: number;
  history: DeliveryAttempt[];
  /** Device-clock ms before which no attempt may be made (backoff). */
  nextAtMs: number;
  /** An explicit, per-report consent tap (§4, outside dev mode). */
  consented: boolean;
}

/** How many delivery attempts a record remembers. Enough to show a night of
 *  retries; capped so history cannot outgrow the report it describes. */
export const MAX_DELIVERY_HISTORY = 12;

/**
 * THE bytes. Pure in the record, so `preview()` and the uploader call the same
 * function with the same argument and get the same string — that byte-identity
 * is the whole content of the §4 promise.
 *
 * The record's `queuedAt`/`attempts`/`history` — "how long it spent trying"
 * (§5 rung 4) — are deliberately NOT here: the intake rejects any field it does
 * not know, so emitting them would cost the entire report. They persist on the
 * record, they ride rung 5 (the user copying it), and turning them on over the
 * wire is one additive server field away. See the job report.
 */
export function renderUpload(report: QueuedReport): string {
  return JSON.stringify(report.body);
}

/**
 * A human-readable rendering for rung 5 — the user copying a report through a
 * channel they can still reach. It is the uploaded document PLUS the delivery
 * history, because a person reading it wants to know how long it spent trying,
 * and a person is not a strict JSON decoder.
 */
export function renderForCopy(report: QueuedReport): string {
  return JSON.stringify(
    {
      ...report.body,
      reason: report.reason,
      delivery: {
        queuedAt: report.queuedAt,
        attempts: report.attempts,
        history: report.history.slice(-MAX_DELIVERY_HISTORY),
      },
    },
    null,
    2,
  );
}

/**
 * The §3 gate. A document that still contains a forbidden shape is DROPPED, not
 * scrubbed: silently mangling a leak leaves the leak's cause in place, and a
 * missing report is a nuisance while a leaked one is a broken promise (§9).
 */
export function isSafeToUpload(bytes: string): boolean {
  return !containsResidue(bytes);
}

// ---------------------------------------------------------------------------
// Batching
// ---------------------------------------------------------------------------

/** Pull the structured half out of ring lines. The prose `msg` is NEVER taken:
 *  it is written for a human on the device and has no redaction contract. */
export function eventsFromLines(lines: readonly LogLine[], afterTs: number): ReportEvent[] {
  const out: ReportEvent[] = [];
  for (const line of lines) {
    if (line.event === undefined || line.ts <= afterTs) continue;
    out.push({ at: line.ts, payload: line.event });
  }
  return out;
}

/** `diag.DefaultMaxEvents` — the intake rejects a report carrying more. */
export const MAX_EVENTS_PER_REPORT = 500;

/**
 * `diag.minEpoch` / `diag.maxEpoch` (schema.go). A device whose clock has not
 * been set yet — the first seconds after a cold boot, before NTP — stamps
 * events in 1970, and the intake rejects the WHOLE report for one such event.
 * A report that can only ever be refused is worse than no report: it burns the
 * retry ladder of a device that may be blocked, which is the one budget the
 * store-and-forward rung is trying to spend well.
 */
export const MIN_EVENT_EPOCH = 1_577_836_800; // 2020-01-01
export const MAX_EVENT_EPOCH = 4_102_444_800; // 2100-01-01

/**
 * The events a report may actually carry: those the intake would not reject
 * categorically, newest kept when there are too many. Applied BEFORE queueing,
 * because "queued but unsendable" is indistinguishable from "blocked" on the
 * device and identical to "dropped" everywhere else.
 */
export function usableEvents(events: readonly ReportEvent[]): ReportEvent[] {
  const dated = events.filter((e) => {
    const at = coarseSecond(e.at);
    return at >= MIN_EVENT_EPOCH && at <= MAX_EVENT_EPOCH;
  });
  return dated.length > MAX_EVENTS_PER_REPORT ? dated.slice(-MAX_EVENTS_PER_REPORT) : dated;
}

/** Coarse minute helper re-exported for the queue's own stamps. */
export { coarseMinute };

// ---------------------------------------------------------------------------
// Identity of a report
// ---------------------------------------------------------------------------

// 128 bits, lowercase hex, exactly as `diag.reportIDRe` requires. Deliberately
// NOT a UUID: §3 forbids UUIDs in anything that leaves the device, and an id
// that LOOKED like one would be stopped by our own gate — correctly, since
// nothing downstream can tell a report id from a call id by looking.
const REPORT_ID_BYTES = 16;
export const REPORT_ID_LENGTH = REPORT_ID_BYTES * 2;

export function randomReportId(random?: () => number): string {
  const bytes = new Uint8Array(REPORT_ID_BYTES);
  const webcrypto = (globalThis as { crypto?: { getRandomValues?: (a: Uint8Array) => void } }).crypto;
  if (random === undefined && typeof webcrypto?.getRandomValues === 'function') {
    webcrypto.getRandomValues(bytes);
  } else {
    const rnd = random ?? Math.random;
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(rnd() * 256) & 0xff;
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += (bytes[i] as number).toString(16).padStart(2, '0');
  return out;
}
